import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaTshirt, FaRulerVertical, FaUserTie, FaGem } from 'react-icons/fa';

const ServicesSection = styled.section`
  padding: 5rem 0;
  background-color: var(--color-secondary);
`;

const ServicesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -10px;
    width: 60px;
    height: 3px;
    background-color: var(--color-accent);
    transform: translateX(-50%);
  }
`;

const SectionSubtitle = styled(motion.span)`
  font-size: 1rem;
  color: var(--color-accent);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 1rem;
  display: block;
`;

const SectionDescription = styled(motion.p)`
  max-width: 700px;
  margin: 0 auto;
  color: var(--color-text-secondary);
  font-size: 1.1rem;
  line-height: 1.6;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const ServiceCard = styled(motion.div)`
  background-color: var(--color-primary);
  padding: 2rem;
  border-radius: 5px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  }
`;

const ServiceIcon = styled.div`
  font-size: 2.5rem;
  color: var(--color-accent);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-color: rgba(212, 175, 55, 0.1);
  margin: 0 auto 1.5rem;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const ServiceDescription = styled.p`
  color: var(--color-text-secondary);
  text-align: center;
  line-height: 1.6;
`;

const ServicePrice = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  font-weight: bold;
  color: var(--color-accent);
  font-size: 1.2rem;
`;

const Services: React.FC = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const services = [
    {
      icon: <FaTshirt />,
      title: "Aluguel de Ternos",
      description: "Ampla variedade de ternos para aluguel em diferentes cores, tecidos e tamanhos para todas as ocasiões.",
      price: "A partir de R$ 250,00"
    },
    {
      icon: <FaUserTie />,
      title: "Aluguel de Smoking",
      description: "Smokings elegantes e sofisticados para eventos formais e casamentos, disponíveis em diversos modelos.",
      price: "A partir de R$ 690,00"
    },
    {
      icon: <FaRulerVertical />,
      title: "Ajustes Personalizados",
      description: "Serviço de ajuste para garantir que seu traje tenha o caimento perfeito para o seu corpo.",
      price: "Incluído no aluguel"
    },
    {
      icon: <FaGem />,
      title: "Acessórios Completos",
      description: "Complementos como gravatas, lenços, abotoaduras e coletes para completar seu visual com elegância.",
      price: "Preços variados"
    }
  ];

  return (
    <ServicesSection id="services" ref={ref}>
      <ServicesContainer>
        <SectionHeader>
          <SectionSubtitle
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            Nossos Serviços
          </SectionSubtitle>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            O que oferecemos
          </SectionTitle>
          <SectionDescription
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Proporcionamos soluções completas em trajes masculinos para todos os tipos de eventos,
            com foco na qualidade e na satisfação dos nossos clientes.
          </SectionDescription>
        </SectionHeader>
        
        <ServicesGrid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {services.map((service, index) => (
            <ServiceCard key={index} variants={itemVariants}>
              <ServiceIcon>{service.icon}</ServiceIcon>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
              <ServicePrice>{service.price}</ServicePrice>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </ServicesContainer>
    </ServicesSection>
  );
};

export default Services; 